/**
 * FluxabilityMail domain resolution and browser sign-in bridge. Only configured deployed mail
 * domains can produce a bridge; historical hostname mappings and OAuth storage keys support
 * retained installations.
 *
 * @module apps/web/src/lib/mailBridge
 */

/**
 * Resolved mail domain and HTTPS app origin, with an optional company slug from the planner
 * hostname.
 */
export interface MailBridge {
  /** Allowed deployed mail domain selected for the sign-in bridge. */
  domain: string;
  /** HTTPS origin of the corresponding FluxabilityMail application. */
  mailBase: string;
  /** Optional planner-host company slug used during domain resolution. */
  companySlug?: string;
}

/** Browser storage key for validating the outstanding mail-bridge callback state. */
const MAIL_OAUTH_STATE_KEY = 'fluxiplanner-mail-oauth-state';
/** Browser storage key for restoring the local route after a mail-bridge login. */
const MAIL_OAUTH_RETURN_KEY = 'fluxiplanner-mail-oauth-return';

/**
 * Resolve an explicitly selected domain or the current planner host to an allowed deployed mail
 * origin. Returns null for unsupported, local, or preview hosts.
 */
export function getMailBridge(domainOverride?: string | null): MailBridge | null {
  if (domainOverride) {
    const domain = normalizeMailDomain(domainOverride);
    if (!domain || !deployedMailBridgeDomains().includes(domain)) return null;
    return { domain, mailBase: `https://mail.${domain}` };
  }

  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith('.pages.dev') || host === 'localhost' || host === '127.0.0.1') return null;

  const resolved = resolveMailDomainForHost(host);
  if (!resolved?.domain || resolved.domain.endsWith('.pages.dev')) return null;
  if (!deployedMailBridgeDomains().includes(resolved.domain)) return null;

  return {
    domain: resolved.domain,
    mailBase: `https://mail.${resolved.domain}`,
    companySlug: resolved.companySlug,
  };
}

/**
 * Return the resolved bridge domain, falling back to fluxability.io.
 */
export function getDefaultMailDomain(): string {
  return getMailBridge()?.domain || 'fluxability.io';
}

/**
 * Read the configured deployed mail-domain allowlist, defaulting to Fluxability and Fluxiplanner
 * domains.
 */
export function getAvailableMailDomains(): string[] {
  return deployedMailBridgeDomains();
}

/**
 * Normalize user input by trimming, lowercasing, and removing scheme, mail prefix, and path. This
 * normalization alone is not an allowlist check.
 */
export function normalizeMailDomain(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^mail\./, '')
    .replace(/\/.*$/, '');
}

/**
 * Store a one-time state and return path, then navigate to the allowed Mail origin with planner
 * callback parameters. Throws when the requested mail domain is not deployed.
 */
export function startFluxabilityMailOAuth(domainInput: string, returnTo = '/settings'): void {
  const bridge = getMailBridge(domainInput);
  if (!bridge) {
    throw new Error('Choose a FluxabilityMail domain that is connected to Fluxability.');
  }

  const state = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(MAIL_OAUTH_STATE_KEY, state);
  sessionStorage.setItem(MAIL_OAUTH_RETURN_KEY, returnTo.startsWith('/') ? returnTo : '/settings');

  const callback = new URL('/auth/mail/callback', window.location.origin);
  const target = new URL('/', bridge.mailBase);
  target.searchParams.set('flex_return', callback.toString());
  target.searchParams.set('flex_domain', bridge.domain);
  target.searchParams.set('flex_state', state);
  window.location.assign(target.toString());
}

/**
 * Consume a mail_token URL fragment, checking state when a stored expected state exists, clearing bridge state, and removing the fragment from browser history. Returns null when no mail token is present.
 */
export function consumeFluxabilityMailOAuthCallback(): { /** Mail bridge credential read from the callback fragment for planner session exchange. */ token: string; /** Normalized mail domain from the callback when supplied. */ domain?: string; /** Callback state compared with the stored expected value when one exists. */ state?: string; /** Stored local route restored after consuming the bridge callback. */ returnTo?: string } | null {
  if (typeof window === 'undefined' || !window.location.hash) return null;
  const hash = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(hash);
  const token = params.get('mail_token');
  if (!token) return null;

  const state = params.get('mail_state') || undefined;
  const expectedState = sessionStorage.getItem(MAIL_OAUTH_STATE_KEY);
  const returnTo = sessionStorage.getItem(MAIL_OAUTH_RETURN_KEY) || '/settings';
  sessionStorage.removeItem(MAIL_OAUTH_STATE_KEY);
  sessionStorage.removeItem(MAIL_OAUTH_RETURN_KEY);
  if (expectedState && state !== expectedState) {
    throw new Error('FluxabilityMail sign-in state did not match. Please try again.');
  }

  const domain = normalizeMailDomain(params.get('mail_domain') || '');
  window.history.replaceState(null, '', '/auth/mail/callback');
  return { token, domain: domain || undefined, state, returnTo };
}

/** Map recognized current and legacy planner hosts to their configured mail bridge domain. Unknown company slugs or unsupported host patterns return null; this mapping alone does not establish a deployed bridge. */
function resolveMailDomainForHost(host: string): { /** Mail domain resolved from an explicitly recognized planner hostname. */ domain: string; /** Optional organization slug derived from the recognized planner host. */ companySlug?: string } | null {
  if (host === 'fluxability.io' || host === 'www.fluxability.io') {
    return { domain: 'fluxability.io', companySlug: 'central' };
  }

  if (host === 'fluxiplanner.com' || host === 'www.fluxiplanner.com') {
    return { domain: 'sahastasai.com', companySlug: 'central' };
  }

  if (host.endsWith('.fluxability.io')) {
    const companySlug = host.slice(0, -'.fluxability.io'.length);
    const domain = fluxCompanyDomains()[companySlug];
    if (!domain) return null;
    return { domain, companySlug };
  }

  if (host.endsWith('.fluxiplanner.com')) {
    const companySlug = host.slice(0, -'.fluxiplanner.com'.length);
    const domain = fluxCompanyDomains()[companySlug];
    if (!domain) return null;
    return { domain, companySlug };
  }

  if (host.startsWith('flexiplanner.')) {
    return { domain: host.slice('flexiplanner.'.length) };
  }

  return null;
}

/** Parse the configured comma-separated company-slug/domain pairs, falling back to retained compatibility mappings. Empty entries are ignored and later duplicate slugs replace earlier values. */
function fluxCompanyDomains(): Record<string, string> {
  const configured = import.meta.env.VITE_FLUX_COMPANY_DOMAINS;
  const raw =
    configured ||
    'central:fluxability.io,sahastasai:sahastasai.com,hydroterrace:hydroterrace.com,sahanavsai:sahanavsai.com,sahasta:sahasta.com,saimithrallc:saimithrallc.com';
  return raw
    .split(',')
    .map(pair => pair.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, pair) => {
      const [slug, domain] = pair.split(':').map(part => part?.trim().toLowerCase());
      if (slug && domain) acc[slug] = domain;
      return acc;
    }, {});
}

/** Parse the configured bridge deployment allowlist, defaulting to the company and legacy planner domains. Normalize case and whitespace before the caller checks a resolved domain. */
function deployedMailBridgeDomains(): string[] {
  const configured = import.meta.env.VITE_MAIL_BRIDGE_DOMAINS;
  const domains = configured || 'fluxability.io,fluxiplanner.com';
  return domains
    .split(',')
    .map(domain => domain.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Request a planner bridge token from the selected Mail app using its cookie session. Throws a sign-in or service error when the response does not include a token.
 */
export async function requestMailFlexToken(bridge: MailBridge): Promise<string> {
  const response = await fetch(`${bridge.mailBase}/api/auth/flex-token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain: bridge.domain }),
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.token) {
    const message =
      payload?.error ||
      (response.status === 401
        ? `Sign in at mail.${bridge.domain}, then return to Fluxability.`
        : `Mail app bridge is not available for ${bridge.domain}.`);
    throw new Error(message);
  }
  return payload.token;
}
